import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { RelocationSandbox } from 'app/core/sandboxes/relocation.sandbox';
import { AzureMonitoringService } from 'app/core/services/AzureMonitoringService';
import { OrderRestService } from 'app/core/services/rest/order.rest.service';
import { RelocationRestService } from 'app/core/services/rest/relocation.rest.service';
import { TemplateRestService } from 'app/core/services/rest/template.rest.service';
import { AbstractEffects } from 'app/core/store/AbstractEffects';
import { RootState } from 'app/core/store/index';
import { Observable, of } from 'rxjs';
import * as actions from './calendar-relocations.actions';
import { CalendarRelocationsLoadAction } from './calendar-relocations.actions';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';

@Injectable({ providedIn: 'root' })
export class CalendarRelocationsEffects extends AbstractEffects {
  loadRelocations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CalendarRelocationsLoadAction>(actions.CALENDAR_RELOCATIONS_LOAD),
      debounceTime(50),
      switchMap(
        (
          action: CalendarRelocationsLoadAction // Register to this event, unregister previous if new event occurs.
        ) =>
          this.relocationRestService
            .getRelocationsForCalendar(action.payload.filter) // Request orders
            // expected:   unloadingDate: array of string; totalNumberOfOrders: number;  florderInfos: FlorderInfo[];
            .pipe(
              map(daysWithFlordersInfoTO => new actions.CalendarRelocationsLoadSuccessAction({ dates: daysWithFlordersInfoTO })),
              catchError(err => {
                if (err.error?.Message?.indexOf('No orders found for given criteria') >= 0) {
                  return of(
                    new actions.CalendarRelocationsLoadSuccessAction({
                      dates: {
                        legend: {},
                        dates: [],
                      },
                    })
                  );
                } else {
                  return this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.CalendarRelocationsLoadFailAction(err))]);
                }
              })
            )
      )
    )
  );
  requestRelocationUnloadingDatesForTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CALENDAR_RELOCATIONS_REQUEST_RELOCATION_UNLOADING_DATES),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        // template given
        return this.templateRestService.getTemplateRelocationUnloadingDates(state.calendar.relocations.selectedTemplate, state.calendar.relocations.UI.startDate).pipe(
          map(result => new actions.CalendarRelocationsRequestRelocationUnloadingDatesSuccessAction(result)),
          catchError(err => this.handleFail(err, 'ERRORS.TEMPLATE.CHECK_DELIVERY_DATES', [of(new actions.CalendarRelocationsRequestRelocationUnloadingDatesFailAction(err))]))
        );
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private orderRestService: OrderRestService,
    private relocationRestService: RelocationRestService,
    private templateRestService: TemplateRestService,
    translateService: TranslateService,
    private sandbox: RelocationSandbox,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
